import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Pagination,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaExclamationCircle, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import { Formik } from 'formik';
import Header from '~/Components/Header';
import 'bootstrap/dist/css/bootstrap.min.css';
import api from '~/Services/api';
import { toast } from 'react-toastify';
import format from '~/Utils/format';
import { salvarEmExcel } from '~/Utils/Helpers';
import Select from 'react-select';

export const RelatorioSolicitacoes = () => {
  const [dados, setDados] = useState([]);
  const [dadosExcel, setDadosExcel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [departamentos, setDepartamentos] = useState([]);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const totalPages = Math.ceil(dados.length / recordsPerPage);

  const dadosOrdenados = React.useMemo(() => {
    if (sortConfig.key) {
      const sorted = [...dados].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return dados;
  }, [dados, sortConfig]);

  const currentRecords = React.useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return dadosOrdenados.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [dadosOrdenados, currentPage, recordsPerPage]);

  const handleSort = column => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  const renderSortIcon = column => {
    if (sortConfig.key !== column) {
      return <FaSort className="ml-1 text-muted" />;
    }
    return sortConfig.direction === 'asc' ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  useEffect(() => {
    const buscarDepartamentos = async () => {
      try {
        setLoadingDepartamentos(true);
        const response = await api.buscarDepartamentos();

        if (response) {
          setDepartamentos([
            { value: '', label: 'Todos' },
            ...response.map(item => ({
              value: item.codigo,
              label: `${item.codigo} - ${item.descricao}`,
            })),
          ]);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoadingDepartamentos(false);
      }
    };

    const buscarUsuarios = async () => {
      try {
        setLoadingUsuarios(true);
        const response = await api.buscarUsuarios();
        if (response) {
          setUsuarios([
            { value: '', label: 'Todos' },
            ...response.map(item => ({
              value: item.codigo,
              label: `${item.codigo} - ${item.nome}`,
            })),
          ]);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoadingUsuarios(false);
      }
    };

    const buscarStatus = async () => {
      try {
        setLoadingStatus(true);
        const response = await api.buscarStatusSolicitacao();
        if (response) {
          setStatusOptions([
            { value: '', label: 'Todos' },
            ...response.map(item => ({
              value: item.codigo,
              label: item.descricao,
            })),
          ]);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoadingStatus(false);
      }
    };

    buscarDepartamentos();
    buscarUsuarios();
    buscarStatus();
  }, []);

  const consultarRelatorioSolicitacoes = async filtro => {
    try {
      setLoading(true);

      const filtroFormatado = {
        departamento: filtro.departamento || null,
        solicitante: filtro.solicitante || null,
        status: filtro.status || null,
        data_inicial: filtro.data_inicial || null,
        data_final: filtro.data_final || null,
      };
      let response = await api.buscarRelatorioSolicitacoes(filtroFormatado);

      if (response) {
        setDados(response.solicitacoes);
        setDadosExcel(response);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  };

  const exportarExcel = async () => {
    try {
      setExportandoExcel(true);
      await salvarEmExcel(
        dadosExcel,
        '/exportador/excel/relatorio-solicitacoes'
      );
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  return (
    <div>
      <Header title="Relatório de Solicitações" />
      <Formik
        initialValues={{
          departamento: '',
          solicitante: '',
          status: '',
          data_inicial: '',
          data_final: '',
        }}
        onSubmit={filtro => {
          consultarRelatorioSolicitacoes(filtro);
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <Form
            onSubmit={handleSubmit}
            className="container-fluid bg-white p-3"
          >
            <Row>
              <Col sm={12} md={2}>
                <Form.Group controlId="departamento">
                  <Form.Label>Departamento</Form.Label>
                  <Select
                    noOptionsMessage={() => 'Nenhum departamento encontrado'}
                    placeholder="Selecione..."
                    isLoading={loadingDepartamentos}
                    isClearable={true}
                    options={departamentos}
                    value={departamentos.find(
                      option => option.value === values.departamento
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'departamento',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={2}>
                <Form.Group controlId="solicitante">
                  <Form.Label>Solicitante</Form.Label>
                  <Select
                    noOptionsMessage={() => 'Nenhum usuário encontrado'}
                    placeholder="Selecione..."
                    isClearable={true}
                    isLoading={loadingUsuarios}
                    options={usuarios}
                    value={usuarios.find(
                      option => option.value === values.solicitante
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'solicitante',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={2}>
                <Form.Group controlId="status">
                  <Form.Label>Status</Form.Label>
                  <Select
                    noOptionsMessage={() => 'Nenhum status encontrado'}
                    placeholder="Selecione..."
                    isClearable={true}
                    isLoading={loadingStatus}
                    options={statusOptions}
                    value={statusOptions.find(
                      option => option.value === values.status
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'status',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={2}>
                <Form.Group controlId="data_inicial">
                  <Form.Label>Data Inicial</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_inicial"
                    value={values.data_inicial}
                    onChange={e => {
                      setFieldValue('data_inicial', e.target.value);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={2}>
                <Form.Group controlId="data_final">
                  <Form.Label>Data Final</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_final"
                    value={values.data_final}
                    min={values.data_inicial}
                    onChange={e => {
                      setFieldValue('data_final', e.target.value);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={2}>
                <Form.Group>
                  <Form.Label>&nbsp;</Form.Label>
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100"
                    disabled={
                      values.data_inicial === '' ||
                      values.data_final === '' ||
                      loading
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <FaSearch className="mr-2" />
                        Buscar
                      </>
                    )}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {dados.length > 0 ? (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row className="mb-3">
              <Col>
                <span>Total de {dados.length} registro(s) encontrado(s).</span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="success"
                  onClick={exportarExcel}
                  disabled={exportandoExcel}
                >
                  {exportandoExcel ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </div>
          <div className="container-fluid bg-white table-responsive">
            <Table className="table table-sm table-striped table-bordered table-hover">
              <thead>
                <tr className="text-center font-size-14">
                  <th
                    onClick={() => handleSort('data_solicitacao')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Data Solicitação {renderSortIcon('data_solicitacao')}
                  </th>
                  <th
                    onClick={() => handleSort('grupo_empresa')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Grupo Empresa {renderSortIcon('grupo_empresa')}
                  </th>
                  <th
                    onClick={() => handleSort('solicitante')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Solicitante {renderSortIcon('solicitante')}
                  </th>
                  <th
                    onClick={() => handleSort('tipo')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Tipo {renderSortIcon('tipo')}
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Status {renderSortIcon('status')}
                  </th>
                  <th
                    onClick={() => handleSort('departamento')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Departamento {renderSortIcon('departamento')}
                  </th>
                  <th
                    onClick={() => handleSort('valor')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Valor {renderSortIcon('valor')}
                  </th>
                  <th
                    onClick={() => handleSort('fornecedor')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Fornecedor {renderSortIcon('fornecedor')}
                  </th>
                </tr>
              </thead>
              <tbody className="text-center font-size-13">
                {currentRecords.map((item, index) => (
                  <tr
                    key={index}
                    style={{
                      fontWeight: 'normal',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s ease',
                    }}
                    className="text-left"
                    onClick={() => {}}
                  >
                    <td>{format.datetime(item.data_solicitacao)}</td>
                    <td>{item.grupo_empresa || 'Não Informado'}</td>
                    <td>{item.solicitante || 'Não Informado'}</td>
                    <td>{item.tipo || 'Não Informado'}</td>
                    <td>{item.status || 'Não Informado'}</td>
                    <td>{item.departamento || 'Não Informado'}</td>
                    <td>{format.money(item.valor)}</td>
                    <td>{item.fornecedor || 'Não Informado'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="container-fluid bg-white p-3">
            <Row className="mt-3 d-flex justify-content-between align-items-center">
              <Col xs="auto">
                {totalPages > 1 && (
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </Col>
            </Row>
          </div>
        </>
      ) : (
        !loading && (
          <div className="container-fluid bg-white p-5 mt-3 text-center">
            <Row>
              <Col>
                <FaExclamationCircle size={48} className="text-muted mb-3" />
                <h5 className="text-muted">Nenhum registro encontrado</h5>
                <p className="text-muted">
                  Utilize os filtros acima para buscar os dados.
                </p>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
};
